import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, fileUrl } from "../lib/api";
import { Button } from "../components/ui/button";
import { Check, MapPin, UserPlus, UserMinus, Users, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../lib/auth";
import { VideoCard } from "../components/VideoCard";

export default function Profile() {
  const { username } = useParams();
  const { user: me } = useAuth();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [videos, setVideos] = useState({ debates: [], thoughts: [] });
  const [tab, setTab] = useState("debates");
  const [followers, setFollowers] = useState([]);
  const [following, setFollowing] = useState([]);
  const [saved, setSaved] = useState([]);

  const load = async () => {
    const u = await api.get(`/users/${username}`);
    setUser(u.data);
    const v = await api.get(`/users/${u.data.id}/videos`);
    setVideos(v.data);
  };
  useEffect(() => { load(); }, [username]);

  useEffect(() => {
    if (!user) return;
    if (tab === "followers") api.get(`/users/${username}/followers`).then((r) => setFollowers(r.data));
    if (tab === "following") api.get(`/users/${username}/following`).then((r) => setFollowing(r.data));
    if (tab === "saved" && me?.id === user.id) api.get(`/bookmarks/detailed`).then((r) => setSaved(r.data));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, user]);

  const toggleFollow = async (id, currentlyFollowing) => {
    try {
      if (currentlyFollowing) await api.post(`/users/${id}/unfollow`);
      else await api.post(`/users/${id}/follow`);
      // Refresh the affected list
      if (tab === "following") { const r = await api.get(`/users/${username}/following`); setFollowing(r.data); }
      if (tab === "followers") { const r = await api.get(`/users/${username}/followers`); setFollowers(r.data); }
    } catch { toast.error("Failed"); }
  };

  if (!user) return <div className="p-10 text-xs font-semibold text-muted-foreground tracking-wider">Loading…</div>;
  const isMe = me?.id === user.id;

  const follow = async () => {
    try {
      if (user.is_following) await api.post(`/users/${user.id}/unfollow`);
      else await api.post(`/users/${user.id}/follow`);
      load();
    } catch { toast.error("Failed"); }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 lg:px-8 py-6 lg:py-10">
      {/* Masthead */}
      <div className="border-b border-border pb-8 mb-8">
        <div className="flex items-start gap-6 flex-wrap">
          <div className="w-24 h-24 rounded-xl bg-secondary border border-border flex items-center justify-center font-mono text-3xl overflow-hidden">
            {user.profile_photo ? (
              <img src={fileUrl(user.profile_photo)} alt="" className="w-full h-full object-cover" />
            ) : (
              user.username?.[0]?.toUpperCase()
            )}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-xs font-semibold text-muted-foreground tracking-wider mb-2 flex items-center gap-3">
              @{user.username}
              {user.verification_status === "verified" && (
                <span className="inline-flex items-center gap-1 border border-[hsl(var(--support))] px-2 py-0.5 text-[hsl(var(--support))]">
                  <Check className="w-3 h-3" /> Verified
                </span>
              )}
            </div>
            <h1 className="font-serif text-4xl lg:text-5xl mb-4">{user.full_name}</h1>
            {user.bio && <p className="text-muted-foreground max-w-xl mb-4">{user.bio}</p>}
            <div className="flex flex-wrap gap-6 text-xs font-semibold text-muted-foreground tracking-wider text-xs">
              <span><strong className="text-foreground text-sm">{user.stats.followers_count}</strong> followers</span>
              <span><strong className="text-foreground text-sm">{user.stats.following_count}</strong> following</span>
              <span><strong className="text-foreground text-sm">{user.stats.debates}</strong> debates</span>
              <span><strong className="text-foreground text-sm">{user.stats.thoughts}</strong> thoughts</span>
            </div>
          </div>
          <div className="flex flex-col gap-2">
            {isMe ? (
              <Link to="/settings">
                <Button variant="outline" className="rounded-xl text-xs font-semibold tracking-wider" data-testid="profile-edit-btn">Edit profile</Button>
              </Link>
            ) : (
              <>
                <Button
                  onClick={follow}
                  className={`rounded-xl text-xs font-semibold tracking-wider ${user.is_following ? "" : "bg-[hsl(var(--primary))] text-white"}`}
                  variant={user.is_following ? "outline" : "default"}
                  data-testid="profile-follow-btn"
                >
                  {user.is_following ? <><UserMinus className="w-4 h-4 mr-2" /> Unfollow</> : <><UserPlus className="w-4 h-4 mr-2" /> Follow</>}
                </Button>
                <Button
                  onClick={() => navigate(`/chats/${user.id}`)}
                  variant="outline"
                  className="rounded-xl text-xs font-semibold tracking-wider"
                  data-testid="profile-message-btn"
                >
                  <MessageCircle className="w-4 h-4 mr-2" /> Message
                </Button>
              </>
            )}
          </div>
        </div>

        {isMe && user.profile_completion < 100 && (
          <div className="mt-6 border border-border p-5" data-testid="profile-completion">
            <div className="flex items-center justify-between mb-2">
              <div className="text-xs font-semibold text-muted-foreground tracking-wider">Profile completion</div>
              <div className="font-mono text-sm">{user.profile_completion}%</div>
            </div>
            <div className="h-1 bg-secondary">
              <div className="h-full bg-[hsl(var(--primary))]" style={{ width: `${user.profile_completion}%` }} />
            </div>
          </div>
        )}
      </div>

      {/* Contribution metrics */}
      <div className="grid grid-cols-2 lg:grid-cols-4 border border-border mb-10">
        {[
          { label: "Total views", value: user.stats.views },
          { label: "Total likes", value: user.stats.likes },
          { label: "Debates", value: user.stats.debates },
          { label: "Thoughts", value: user.stats.thoughts },
        ].map((m, i) => (
          <div key={i} className={`p-6 ${i < 3 ? "border-r border-border" : ""}`} data-testid={`metric-${m.label.toLowerCase().replace(/\s/g, "-")}`}>
            <div className="text-xs font-semibold text-muted-foreground tracking-wider mb-2">{m.label}</div>
            <div className="font-mono text-3xl">{m.value}</div>
          </div>
        ))}
      </div>

      {/* Content tabs */}
      <div className="border border-border rounded-2xl bg-card overflow-hidden">
        <div className="flex overflow-x-auto scrollbar-hide border-b border-border">
          {[
            ["debates", `Debates (${videos.debates?.length || 0})`],
            ["thoughts", `Thoughts (${videos.thoughts?.length || 0})`],
            ["followers", `Followers (${user.stats.followers_count})`],
            ["following", `Following (${user.stats.following_count})`],
            ...(isMe ? [["saved", "Saved"]] : []),
          ].map(([k, label], i) => (
            <button
              key={k}
              onClick={() => setTab(k)}
              data-testid={`profile-tab-${k}`}
              className={`flex-1 min-w-[120px] py-4 text-sm font-semibold tracking-wider transition-colors ${
                tab === k
                  ? "bg-[hsl(var(--primary-soft))] text-[hsl(var(--primary))] border-t-[3px] border-[hsl(var(--primary))]"
                  : "text-muted-foreground hover:bg-muted"
              } ${i > 0 ? "border-l border-l-border" : ""}`}
            >
              {label}
            </button>
          ))}
        </div>
        <div className="p-6">
          {(tab === "debates" || tab === "thoughts") && (
            videos[tab]?.length === 0 ? (
              <div className="p-12 text-center text-sm text-muted-foreground">No {tab} posted yet</div>
            ) : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {videos[tab].map((v) => (
                  <VideoCard key={v.id} item={v} type={tab === "debates" ? "debate" : "thought"} />
                ))}
              </div>
            )
          )}
          {tab === "followers" && (
            followers.length === 0 ? (
              <div className="p-12 text-center text-sm text-muted-foreground">No followers yet</div>
            ) : (
              <div className="divide-y divide-border">
                {followers.map((u) => (
                  <div key={u.id} className="flex items-center gap-3 py-3" data-testid={`follower-row-${u.username}`}>
                    <Link to={`/profile/${u.username}`} className="w-10 h-10 rounded-full bg-[hsl(var(--primary-soft))] flex items-center justify-center overflow-hidden">
                      {u.profile_photo ? <img src={fileUrl(u.profile_photo)} alt="" className="w-full h-full object-cover" /> : <span className="font-bold text-[hsl(var(--primary))]">{u.username?.[0]?.toUpperCase()}</span>}
                    </Link>
                    <Link to={`/profile/${u.username}`} className="flex-1 min-w-0 hover:text-[hsl(var(--primary))]">
                      <div className="text-sm font-semibold">@{u.username}</div>
                      <div className="text-xs text-muted-foreground truncate">{u.full_name}</div>
                    </Link>
                    {me && me.id !== u.id && (
                      <Button variant={u.is_following ? "outline" : "default"}
                        onClick={() => toggleFollow(u.id, u.is_following)}
                        className={`rounded-full ${u.is_following ? "" : "bg-[hsl(var(--primary))] text-white hover:bg-[hsl(var(--primary))]/90"}`}
                        data-testid={`follower-follow-${u.username}`}
                      >{u.is_following ? "Following" : "Follow"}</Button>
                    )}
                  </div>
                ))}
              </div>
            )
          )}
          {tab === "following" && (
            following.length === 0 ? (
              <div className="p-12 text-center text-sm text-muted-foreground">Not following anyone yet</div>
            ) : (
              <div className="divide-y divide-border">
                {following.map((u) => (
                  <div key={u.id} className="flex items-center gap-3 py-3" data-testid={`following-row-${u.username}`}>
                    <Link to={`/profile/${u.username}`} className="w-10 h-10 rounded-full bg-[hsl(var(--primary-soft))] flex items-center justify-center overflow-hidden">
                      {u.profile_photo ? <img src={fileUrl(u.profile_photo)} alt="" className="w-full h-full object-cover" /> : <span className="font-bold text-[hsl(var(--primary))]">{u.username?.[0]?.toUpperCase()}</span>}
                    </Link>
                    <Link to={`/profile/${u.username}`} className="flex-1 min-w-0 hover:text-[hsl(var(--primary))]">
                      <div className="text-sm font-semibold">@{u.username}</div>
                      <div className="text-xs text-muted-foreground truncate">{u.full_name}</div>
                    </Link>
                    {isMe && (
                      <Button variant="outline" onClick={() => toggleFollow(u.id, true)} className="rounded-full" data-testid={`unfollow-${u.username}`}>
                        Unfollow
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            )
          )}
          {tab === "saved" && isMe && (
            saved.length === 0 ? (
              <div className="p-12 text-center text-sm text-muted-foreground">No saved posts yet — tap the bookmark on any card.</div>
            ) : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {saved.map((s) => (
                  <VideoCard key={`${s._type}-${s.id}`} item={s} type={s._type} />
                ))}
              </div>
            )
          )}
        </div>
      </div>
    </div>
  );
}
